/**
 * Transforms nested approval data into flat array with full-width group header rows
 * suitable for AG Grid display with custom group headers.
 */

interface MilestoneObject {
  _id: string;
  assignment_id: string;
  brand_id: {
    _id: string;
    name: string;
  };
  campaign_id: {
    _id: string;
    name: string;
    shortId: string;
    department: string;
    fundedAmount: number;
    amountPaidToCreators: number;
    enableAccountSegmentation: boolean;
  };
  user_id: {
    _id: string;
    name: string;
    email: string;
  };
  name: string;
  status: string;
  priority: string;
  rate: number;
  holdBalance: number;
  paidBalance: number;
  dateCompleted: Date | null;
  isComplete: boolean;
  releasePayment: boolean;
  isPaymentApproved: boolean;
  dueDate: Date | null;
  nextPaymentDate: Date | null;
}

interface CampaignGroup {
  name: string; // campaign name (from item.campaign_id.name)
  totalAmount: number; // total holdBalance for this campaign in this bucket
  approvedAmount: number; // approved holdBalance for this campaign in this bucket
  unapprovedAmount: number; // unapproved holdBalance for this campaign in this bucket
  count: number; // total number of items for this campaign in this brand/bucket
  approvedCount: number; // number of approved items for this campaign in this brand/bucket
  unapprovedCount: number; // number of unapproved items for this campaign in this brand/bucket
  items: MilestoneObject[]; // raw items for this campaign (these are the milestones to display)
}

interface BrandGroup {
  name: string; // brand name (from item.brand_id.name)
  totalAmount: number; // total holdBalance for this brand in this bucket
  approvedAmount: number; // approved holdBalance for this brand in this bucket
  unapprovedAmount: number; // unapproved holdBalance for this brand in this bucket
  count: number; // total number of items for this brand in this bucket
  approvedCount: number; // number of approved items for this brand in this bucket
  unapprovedCount: number; // number of unapproved items for this brand in this bucket
  items: MilestoneObject[]; // raw items for this brand in this bucket (don't display)
  byCampaign: CampaignGroup[]; // sub-groups for this brand, grouped by campaign
}

interface ApprovalObject {
  name: string; // "YYYY-MM-01" | "YYYY-MM-15" | "unknown" - date bucket name
  totalAmount: number; // total holdBalance for the bucket
  approvedAmount: number; // approved holdBalance for the bucket
  unapprovedAmount: number; // unapproved holdBalance for the bucket
  count: number; // total number of items in this bucket
  approvedCount: number; // number of approved items in this bucket
  unapprovedCount: number; // number of unapproved items in this bucket
  items: MilestoneObject[]; // raw items belonging to this bucket (do not display)
  byBrand: BrandGroup[]; // sub-groups for this date group, grouped by brand
}

// Row types for AG Grid
export interface DateGroupRow {
  _id: string;
  rowType: "dateGroup";
  date: string;
  totalAmount: number;
  approvedAmount: number;
  unapprovedAmount: number;
  count: number;
  approvedCount: number;
  unapprovedCount: number;
  items: MilestoneObject[]; // All milestones in this date group
}

export interface BrandGroupRow {
  _id: string;
  rowType: "brandGroup";
  brandName: string;
  brandId: string;
  totalAmount: number;
  approvedAmount: number;
  unapprovedAmount: number;
  count: number;
  approvedCount: number;
  unapprovedCount: number;
  parentDate: string;
  items: MilestoneObject[]; // All milestones in this brand group
}

export interface CampaignGroupRow {
  _id: string;
  rowType: "campaignGroup";
  campaignName: string;
  campaignId: string;
  totalAmount: number;
  approvedAmount: number;
  unapprovedAmount: number;
  count: number;
  approvedCount: number;
  unapprovedCount: number;
  parentBrand: string;
  parentDate: string;
  items: MilestoneObject[]; // All milestones in this campaign group
}

export interface MilestoneRow extends MilestoneObject {
  rowType: "milestone";
  parentCampaign: string;
  parentBrand: string;
  parentDate: string;
}

export type GridRowData =
  | DateGroupRow
  | BrandGroupRow
  | CampaignGroupRow
  | MilestoneRow;

/**
 * Transforms approval data into flat array with group headers and milestone rows
 * All groups are displayed (no expansion/collapse functionality)
 */
export function transformApprovalData(
  approvalData: ApprovalObject[],
): GridRowData[] {
  const rows: GridRowData[] = [];

  approvalData.forEach((dateGroup) => {
    const dateKey = dateGroup.name; // "YYYY-MM-01", "YYYY-MM-15", or "unknown"

    // Add date group header (every date group gets its own full width row)
    rows.push({
      _id: `date-${dateKey}`, // Add unique ID for AG Grid
      rowType: "dateGroup",
      date: dateGroup.name,
      totalAmount: dateGroup.totalAmount,
      approvedAmount: dateGroup.approvedAmount,
      unapprovedAmount: dateGroup.unapprovedAmount,
      count: dateGroup.count,
      approvedCount: dateGroup.approvedCount,
      unapprovedCount: dateGroup.unapprovedCount,
      items: dateGroup.items || [], // Include all milestones for this date group
    });

    // Add all brand groups
    dateGroup.byBrand?.forEach((brandGroup) => {
      // Add brand group header (each brand gets its own full width row)
      rows.push({
        _id: `brand-${dateKey}-${brandGroup.name}`, // Add unique ID for AG Grid
        rowType: "brandGroup",
        brandName: brandGroup.name,
        brandId: brandGroup.items?.[0]?.brand_id?._id || "",
        totalAmount: brandGroup.totalAmount,
        approvedAmount: brandGroup.approvedAmount,
        unapprovedAmount: brandGroup.unapprovedAmount,
        count: brandGroup.count,
        approvedCount: brandGroup.approvedCount,
        unapprovedCount: brandGroup.unapprovedCount,
        parentDate: dateKey,
        items: brandGroup.items || [], // Include all milestones for this brand group
      });

      // Add all campaign groups
      brandGroup.byCampaign?.forEach((campaignGroup) => {
        // Add campaign group header (each campaign gets its own full width row)
        rows.push({
          _id: `campaign-${dateKey}-${brandGroup.name}-${campaignGroup.name}`, // Add unique ID for AG Grid
          rowType: "campaignGroup",
          campaignName: campaignGroup.name,
          campaignId: campaignGroup.items?.[0]?.campaign_id?._id || "",
          totalAmount: campaignGroup.totalAmount,
          approvedAmount: campaignGroup.approvedAmount,
          unapprovedAmount: campaignGroup.unapprovedAmount,
          count: campaignGroup.count,
          approvedCount: campaignGroup.approvedCount,
          unapprovedCount: campaignGroup.unapprovedCount,
          parentBrand: brandGroup.name,
          parentDate: dateKey,
          items: campaignGroup.items || [], // Include all milestones for this campaign group
        });

        // Add all milestones under this campaign
        // These are the actual milestones to display (from campaign-level items)
        campaignGroup.items?.forEach((milestone) => {
          rows.push({
            ...milestone,
            rowType: "milestone",
            parentCampaign: campaignGroup.name,
            parentBrand: brandGroup.name,
            parentDate: dateKey,
          });
        });
      });

      // NOTE: Do NOT display brandGroup.items - these are for aggregation only
    });

    // NOTE: Do NOT display dateGroup.items - these are for aggregation only
  });

  return rows;
}

/**
 * AG Grid callback to determine if a row should be full width
 * According to AG Grid docs: receives params object containing rowNode
 */
export function isFullWidthRow(params: {
  rowNode?: { data?: GridRowData };
  data?: GridRowData;
}): boolean {
  // Handle both rowNode.data and direct data access patterns
  const data = params?.rowNode?.data || params?.data;

  if (!data) {
    return false;
  }

  // Return true for group headers, false for milestone rows
  const isFullWidth = data.rowType !== "milestone";
  return isFullWidth;
}

// Removed: toggleGroupExpansion function - no longer needed without expand/collapse

/**
 * Calculate summary statistics from approval data
 */
export function calculateApprovalSummary(approvalData: ApprovalObject[]) {
  const totalCount = approvalData.reduce((sum, group) => sum + group.count, 0);
  const approvedCount = approvalData.reduce(
    (sum, group) => sum + group.approvedCount,
    0,
  );
  const unapprovedCount = approvalData.reduce(
    (sum, group) => sum + group.unapprovedCount,
    0,
  );
  const totalAmount = approvalData.reduce(
    (sum, group) => sum + group.totalAmount,
    0,
  );
  const approvedAmount = approvalData.reduce(
    (sum, group) => sum + group.approvedAmount,
    0,
  );
  const unapprovedAmount = approvalData.reduce(
    (sum, group) => sum + group.unapprovedAmount,
    0,
  );
  const groupCount = approvalData.length;

  return {
    totalCount,
    approvedCount,
    unapprovedCount,
    totalAmount,
    approvedAmount,
    unapprovedAmount,
    groupCount,
  };
}
